import React, { useState, useRef, useEffect } from 'react';
import { hasPermission } from '@/utils/permission-user-utils';
import UserSearchDialog from './dialog/UserSearchDialog';
import CreateGroupDialog from './dialog/CreateGroupDialog';
import { GroupInfo } from '@/api/types';
import { usePageDataStore } from '@/stores/usePageDataStore';
import { ContactPageTab } from '@/stores/usePageDataStore';

interface ContactSearchProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  onTabChange: (tab: ContactPageTab) => void
  onCreateGroupSuccess?: (group: GroupInfo) => void
}

export default function ContactSearch({
  searchQuery,
  onSearchChange,
  onTabChange,
  onCreateGroupSuccess
}: ContactSearchProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUserSearchDialog, setShowUserSearchDialog] = useState(false);
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [searchDialogInitialQuery, setSearchDialogInitialQuery] = useState('');
  const groupActiveTab = usePageDataStore(state => state.groupActiveTab)

  const dropdownRef = useRef<HTMLDivElement>(null);

  // 检查权限
  const canCreateGroup = hasPermission('CREATE_GROUP');

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * 处理回车键搜索用户
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      setSearchDialogInitialQuery(searchQuery);
      setShowUserSearchDialog(true);
    }
  };

  /**
   * 处理添加好友
   */
  const handleAddFriend = () => {
    setSearchDialogInitialQuery('');
    setShowUserSearchDialog(true);

    setShowDropdown(false);
  };

  /**
   * 处理创建群组
   */
  const handleCreateGroup = () => {
    if (canCreateGroup) {
      setShowCreateGroupDialog(true);
    }
    setShowDropdown(false);
  };

  /**
   * 渲染下拉菜单项
   */
  const renderDropdownItem = (
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
    disabled: boolean = false
  ) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center px-3 py-2 text-sm text-left hover:bg-gray-100 transition-colors ${disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'
        }`}
    >
      <span className="mr-2">{icon}</span>
      {label}
    </button>
  );
  return (
    <>
      <div className="space-y-4">
        {/* 搜索框 */}
        <div className="w-full flex flex-row" ref={dropdownRef}>
          <div className='relative flex-1'>
            <input
              type="text"
              placeholder="搜索好友..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full pl-8 pr-10 py-2 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
            />
            <svg className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className='relative'>
            {/* 添加按钮 */}
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="ml-3 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg bg-gray-50 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>

            {/* 下拉菜单 */}
            {showDropdown && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="py-1">
                  {renderDropdownItem(
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>,
                    '添加好友',
                    handleAddFriend
                  )}

                  {canCreateGroup && renderDropdownItem(
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>,
                    '创建群组',
                    handleCreateGroup
                  )}
                </div>

              </div>
            )}
          </div>
        </div>

        {/* 分组标签 */}
        <div className="flex space-x-0 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => onTabChange(ContactPageTab.FRIENDS)}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${groupActiveTab === ContactPageTab.FRIENDS
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
              }`}
          >
            好友列表
          </button>
          <button
            onClick={() => onTabChange(ContactPageTab.GROUPS)}
            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${groupActiveTab === ContactPageTab.GROUPS
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-800'
              }`}
          >
            群组列表
          </button>
        </div>
      </div>

      {/* 对话框 */}
      {
        showUserSearchDialog && (
          <UserSearchDialog
            open={showUserSearchDialog}
            onClose={() => setShowUserSearchDialog(false)}
            initialSearchQuery={searchDialogInitialQuery}
          />
        )
      }

      {
        showCreateGroupDialog && (
          <CreateGroupDialog
            open={showCreateGroupDialog}
            onClose={() => setShowCreateGroupDialog(false)}
            onCreateGroupSuccess={onCreateGroupSuccess}
          />
        )
      }

    </>
  )
}
